"use client"

import { useState } from "react"
import { createRoute } from "@tanstack/react-router"
import { Plus } from "lucide-react"

import { PageAction, PageHeader } from "@/components/graphite/page-header"
import { PlacementDetailDialog } from "@/components/placement-detail-dialog"
import { PlacementDialog } from "@/components/placement-dialog"
import { GraphitePlacements } from "@/components/placements/graphite-placements"
import { FEE_BAND_OPTIONS, usePlacements, type PlacementRow } from "@/hooks/use-placements"
import MainAppRoute from "@/rootRoutes/MainApp"
import { getPlacementRecordId } from "@/utils/placements"
import type { Placement } from "openapi/api/model"

/** The rows on screen, as a file. Escaped, so a company with a comma survives. */
function exportCsv(rows: PlacementRow[]) {
  const cell = (value: unknown) => {
    const text = value == null ? "" : String(value)
    return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
  }
  const lines = [
    ["candidate", "type", "job", "company", "fee", "start", "end", "term", "splits", "created"].join(","),
    ...rows.map((row) =>
      [
        row.name,
        row.type,
        row.jobTitle,
        row.company,
        row.feeValue,
        row.from ?? "",
        row.to,
        row.term,
        row.splits.join(" | "),
        row.created,
      ]
        .map(cell)
        .join(","),
    ),
  ]

  const url = URL.createObjectURL(
    new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" }),
  )
  const link = document.createElement("a")
  link.href = url
  link.download = `placements_${new Date().toISOString().slice(0, 10)}.csv`
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

/**
 * Placements, from canvas 7c.
 *
 * The page owns the two dialogs and the export; every figure, row and filter
 * comes from `usePlacements`, and `GraphitePlacements` renders what it is
 * handed.
 */
function PlacementsPage() {
  const placements = usePlacements()
  const [selected, setSelected] = useState<Placement | null>(null)
  const [isDetailOpen, setIsDetailOpen] = useState(false)
  const [isCreateOpen, setIsCreateOpen] = useState(false)

  return (
    <div className="@container flex h-full min-h-0 flex-col">
      {/*
        7c writes the subject and the one action in the header, and puts every
        filter in the body — the money is what the page is for, so nothing above
        the fold is a control except the export.
      */}
      <PageHeader
        title="Placements"
        description="Every confirmed placement and its fee."
        meta={`${placements.total} placements`}
        actions={
          <>
            {/* 7c draws only the export here. Creating a placement has to live
                somewhere, and beside it is where every other page puts its
                one primary action. */}
            <PageAction tone="primary" onClick={() => setIsCreateOpen(true)}>
              <Plus className="size-3.5" />
              New Placement
            </PageAction>
            <PageAction onClick={() => exportCsv(placements.rows)}>Export CSV</PageAction>
          </>
        }
      />

      <div className="min-h-0 flex-1">
        <GraphitePlacements
          shares={placements.shares}
          revenue={placements.revenue}
          companyCount={placements.companyCount}
          periodLabel={placements.periodLabel}
          rows={placements.rows}
          inPeriodCount={placements.inPeriodCount}
          rangeRevenue={placements.rangeRevenue}
          companies={placements.companies}
          company={placements.company}
          onCompanyChange={placements.setCompany}
          search={placements.search}
          onSearchChange={placements.setSearch}
          period={placements.period}
          periodOptions={placements.periodOptions}
          onPeriodChange={placements.setPeriod}
          fee={placements.fee}
          feeOptions={FEE_BAND_OPTIONS}
          onFeeChange={placements.setFee}
          from={placements.from}
          to={placements.to}
          onFromChange={placements.setFrom}
          onToChange={placements.setTo}
          onClearPeriod={placements.clearPeriod}
          hasPeriod={placements.hasPeriod}
          onOpen={(row) => {
            setSelected(row.placement)
            setIsDetailOpen(true)
          }}
          isLoading={placements.isLoading}
          isError={placements.isError}
        />
      </div>

      <PlacementDetailDialog
        isOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        placementId={getPlacementRecordId(selected)}
      />

      {isCreateOpen ? (
        <PlacementDialog isOpen={isCreateOpen} onClose={() => setIsCreateOpen(false)} />
      ) : null}
    </div>
  )
}

const PlacementsPageRoute = createRoute({
  getParentRoute: () => MainAppRoute,
  path: "/placements",
  component: () => <PlacementsPage />,
})

export default PlacementsPageRoute
